/**
 * Smart shopping list builder.
 *
 * Takes a list of recipes, parses every ingredient line into
 * `{ qty, unit, name, note }`, normalises the name for matching, merges
 * duplicates where it's safe (same canonical name + same unit), classifies
 * each item into a supermarket department, and returns a single ordered
 * `items[]` array ready to render.
 *
 * Rules:
 * - Same canonical name + same unit → sum quantities ("200 g + 100 g = 300 g").
 * - Same canonical name + different units → keep separate (we never lie
 *   about quantities).
 * - Vague units like "pinch" / "dash" / "to taste" merge into a unit-less
 *   row with the same canonical key, so "Salt" + "Salt, pinch" collapse.
 * - A final pass collapses prefix-matched entries where the longer one ends
 *   in a stripped descriptor — catches "lemon" + "lemon, cut into wedges".
 * - Notes ("finely chopped", "for garnish") are preserved on the merged row.
 * - Display name keeps the most-detailed casing/wording from the source.
 */

import { classifyDepartmentDetailed, type Department } from "./departments";
import { decimalToFraction } from "./fractions";

export type ShoppingItem = {
  id: string;
  name: string;
  quantity: string;
  unit: string;
  note: string;
  department: Department;
  checked: boolean;
  position: number;
  custom: boolean;
  sources: string[]; // recipe ids that contributed
  /** 0–1 classifier confidence; <0.6 surfaces a low-confidence hint in UI. */
  confidence?: number;
};

type ParsedLine = {
  qty: number | null;
  unit: string;
  name: string;
  note: string;
  raw: string;
};

const UNIT_ALIASES: Record<string, string> = {
  g: "g", gram: "g", grams: "g", gr: "g",
  kg: "kg", kilo: "kg", kilos: "kg", kilogram: "kg", kilograms: "kg",
  ml: "ml", millilitre: "ml", millilitres: "ml", milliliter: "ml", milliliters: "ml",
  l: "l", litre: "l", litres: "l", liter: "l", liters: "l",
  tsp: "tsp", teaspoon: "tsp", teaspoons: "tsp",
  tbsp: "tbsp", tablespoon: "tbsp", tablespoons: "tbsp", tbs: "tbsp",
  cup: "cup", cups: "cup",
  oz: "oz", ounce: "oz", ounces: "oz",
  lb: "lb", lbs: "lb", pound: "lb", pounds: "lb",
  clove: "clove", cloves: "clove",
  slice: "slice", slices: "slice",
  pinch: "pinch", pinches: "pinch",
  dash: "dash", dashes: "dash",
  handful: "handful", handfuls: "handful",
  bunch: "bunch", bunches: "bunch",
  can: "can", cans: "can", tin: "can", tins: "can",
  pack: "pack", packs: "pack", packet: "pack", packets: "pack",
  jar: "jar", jars: "jar",
  sprig: "sprig", sprigs: "sprig",
  stick: "stick", sticks: "stick",
};

/** Units that describe vague amounts; merge with unit-less rows of same key. */
const VAGUE_UNITS = new Set(["pinch", "dash", "handful", "sprig"]);

const QTY_RE = /^(\d+\s+\d+\/\d+|\d+\/\d+|\d+(?:[.,]\d+)?(?:\s*[-–]\s*\d+(?:[.,]\d+)?)?)/;

const parseQty = (raw: string): { value: number | null; matchLength: number } => {
  const m = raw.match(QTY_RE);
  if (!m) return { value: null, matchLength: 0 };
  const token = m[1];
  const mixed = token.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (mixed) {
    const v = Number(mixed[1]) + Number(mixed[2]) / Number(mixed[3]);
    return { value: v, matchLength: m[0].length };
  }
  const frac = token.match(/^(\d+)\/(\d+)$/);
  if (frac) {
    return { value: Number(frac[1]) / Number(frac[2]), matchLength: m[0].length };
  }
  const range = token.match(/^(\d+(?:[.,]\d+)?)\s*[-–]\s*(\d+(?:[.,]\d+)?)$/);
  if (range) {
    const lo = Number(range[1].replace(",", "."));
    const hi = Number(range[2].replace(",", "."));
    return { value: (lo + hi) / 2, matchLength: m[0].length };
  }
  const v = Number(token.replace(",", "."));
  if (!Number.isFinite(v)) return { value: null, matchLength: 0 };
  return { value: v, matchLength: m[0].length };
};

const parseUnit = (rest: string): { unit: string; matchLength: number } => {
  const trimmed = rest.trimStart();
  const ws = rest.length - trimmed.length;
  const m = trimmed.match(/^([a-zA-Z]+)\b\.?/);
  if (!m) return { unit: "", matchLength: 0 };
  const candidate = m[1].toLowerCase();
  const canonical = UNIT_ALIASES[candidate];
  if (!canonical) return { unit: "", matchLength: 0 };
  return { unit: canonical, matchLength: ws + m[0].length };
};

/** Split off a trailing note after a comma: "red onion, finely chopped". */
const splitNote = (text: string): { name: string; note: string } => {
  const idx = text.indexOf(",");
  if (idx === -1) return { name: text.trim(), note: "" };
  return {
    name: text.slice(0, idx).trim(),
    note: text.slice(idx + 1).trim(),
  };
};

const parseLine = (raw: string): ParsedLine => {
  const line = raw.trim();
  if (!line) return { qty: null, unit: "", name: "", note: "", raw };

  let cursor = 0;
  const { value: qty, matchLength: qtyLen } = parseQty(line);
  cursor += qtyLen;

  const { unit, matchLength: unitLen } = parseUnit(line.slice(cursor));
  cursor += unitLen;

  const remainder = line.slice(cursor).trim();
  const { name, note } = splitNote(remainder);

  // "Salt, to taste" / "Pepper to taste" should be detected as vague.
  let finalUnit = unit;
  if (!finalUnit && /\bto taste\b/i.test(note)) {
    finalUnit = ""; // keep unitless; vague-merge handles it via note
  }

  return { qty, unit: finalUnit, name, note, raw };
};

/** Trailing descriptor words that describe a *form*, not a different ingredient. */
const TRAILING_DESCRIPTORS = new Set([
  "wedge", "wedges",
  "slice", "slices",
  "half", "halves",
  "quarter", "quarters",
  "pinch", "pinches",
  "dash", "dashes",
  "clove", "cloves",
  "sprig", "sprigs",
  "leaf", "leaves",
  "stick", "sticks",
  "head", "heads",
  "bunch", "bunches",
  "knob", "knobs",
  "piece", "pieces",
  "chunk", "chunks",
  "strip", "strips",
  "cube", "cubes",
  "zest",
  "juice",
  "peel",
  "rind",
  "florets", "floret",
  "ring", "rings",
  "shaving", "shavings",
]);

/** Leading colour words — only stripped when followed by a recognised noun. */
const LEADING_COLOURS = new Set([
  "red", "white", "yellow", "green", "black", "purple",
]);
// Note: "pepper(s)" deliberately omitted — "red pepper" (capsicum) and
// "black pepper" (peppercorn) are different ingredients and must keep their
// colour qualifier in the canonical key so user overrides don't bleed
// across them.
const COLOUR_NOUNS = new Set([
  "onion", "onions", "cabbage", "cabbages",
  "chilli", "chillies", "chili", "chilies", "bean", "beans",
  "lentil", "lentils", "grape", "grapes",
]);
/** Don't strip the colour when it's part of an identity like "red wine vinegar". */
const COLOUR_IDENTITY_FOLLOWERS = new Set([
  "wine", "vinegar", "miso", "rice", "currant", "currants", "tea",
]);

/** Irregular plurals the naive trailing-s strip misses. */
const PLURAL_MAP: Record<string, string> = {
  leaves: "leaf",
  wedges: "wedge",
  cloves: "clove",
  tomatoes: "tomato",
  potatoes: "potato",
  chillies: "chilli",
  chilies: "chili",
  knives: "knife",
  loaves: "loaf",
  halves: "half",
  berries: "berry",
  cherries: "cherry",
  anchovies: "anchovy",
  raspberries: "raspberry",
  strawberries: "strawberry",
  blueberries: "blueberry",
};

/**
 * Lower-case, strip plurals and a small set of leading filler words to
 * produce a key suitable for matching duplicate ingredients across recipes.
 * The original `name` is kept for display.
 */
const canonicalKey = (name: string): string => {
  let key = name.toLowerCase().trim();
  // Strip trailing parenthetical e.g. "(about 2 large)"
  key = key.replace(/\s*\([^)]*\)\s*/g, " ").trim();
  // Common filler suffixes / prep words.
  key = key.replace(
    /\b(fresh|dried|ripe|large|small|medium|extra|finely|roughly|coarsely|chopped|sliced|diced|minced|grated|whole|raw|cooked|peeled|seeded|deseeded|trimmed|crushed|halved|quartered|cubed|shredded|torn|toasted|drained|rinsed)\b/g,
    "",
  );
  key = key.replace(/\s+/g, " ").trim();

  // Strip leading colour qualifier when followed by a recognised noun
  // (and that noun isn't part of a colour-identity phrase like "red wine").
  const tokens = key.split(" ");
  if (tokens.length >= 2 && LEADING_COLOURS.has(tokens[0])) {
    const next = tokens[1];
    const after = tokens[2];
    const isIdentity = after && COLOUR_IDENTITY_FOLLOWERS.has(next);
    if (COLOUR_NOUNS.has(next) && !isIdentity) {
      tokens.shift();
    }
  }

  // Strip trailing descriptor words.
  while (tokens.length > 1 && TRAILING_DESCRIPTORS.has(tokens[tokens.length - 1])) {
    tokens.pop();
  }

  // Depluralise per-token, with irregular map first.
  const result = tokens
    .map((w) => {
      if (PLURAL_MAP[w]) return PLURAL_MAP[w];
      if (w.length > 3 && w.endsWith("s") && !w.endsWith("ss") && !w.endsWith("us")) {
        return w.slice(0, -1);
      }
      return w;
    })
    .join(" ")
    .trim();

  return result;
};

const formatQuantity = (n: number): string => {
  if (!Number.isFinite(n) || n <= 0) return "";
  return decimalToFraction(n);
};

type RecipeForList = {
  id: string;
  ingredient_groups: { name: string; items: string[] }[];
};

type Accumulator = {
  key: string;
  displayName: string;
  qtySum: number | null;
  unit: string;
  notes: Set<string>;
  sources: Set<string>;
  department: Department;
  confidence: number;
};

/**
 * Build a fresh shopping list from a set of recipes. Items are ordered by
 * department (per `DEPARTMENT_ORDER`) and then alphabetically by display name.
 *
 * @param overrides Optional per-user `(canonicalKey → Department)` overrides.
 *                  Used before the context-aware classifier when present.
 */
export const buildShoppingList = (
  recipes: RecipeForList[],
  overrides?: Map<string, Department>,
): ShoppingItem[] => {
  const merged = new Map<string, Accumulator>();

  const classify = (
    key: string,
    rawName: string,
  ): { department: Department; confidence: number } => {
    if (overrides && overrides.has(key)) {
      // User-confirmed override → maximum confidence.
      return { department: overrides.get(key)!, confidence: 1 };
    }
    return classifyDepartmentDetailed(key, rawName);
  };

  const add = (parsed: ParsedLine, recipeId: string) => {
    if (!parsed.name) return;
    const key = canonicalKey(parsed.name);
    if (!key) return;
    const mapKey = `${key}::${parsed.unit}`;
    const existing = merged.get(mapKey);
    if (!existing) {
      const { department, confidence } = classify(key, parsed.name);
      merged.set(mapKey, {
        key,
        displayName: parsed.name,
        qtySum: parsed.qty,
        unit: parsed.unit,
        notes: new Set(parsed.note ? [parsed.note] : []),
        sources: new Set([recipeId]),
        department,
        confidence,
      });
      return;
    }
    if (parsed.name.length > existing.displayName.length) {
      existing.displayName = parsed.name;
    }
    if (parsed.qty != null) {
      existing.qtySum = (existing.qtySum ?? 0) + parsed.qty;
    }
    if (parsed.note) existing.notes.add(parsed.note);
    existing.sources.add(recipeId);
  };

  for (const r of recipes) {
    for (const group of r.ingredient_groups || []) {
      for (const line of group.items || []) {
        const parsed = parseLine(line);
        if (parsed.name) add(parsed, r.id);
      }
    }
  }

  // ---- Vague-unit collapse pass ----
  // For every entry with a vague unit, fold it into the unit-less entry of
  // the same key (creating one if needed). Quantity is dropped, the vague
  // unit becomes part of the note ("plus a pinch").
  for (const [mapKey, acc] of Array.from(merged.entries())) {
    if (!VAGUE_UNITS.has(acc.unit)) continue;
    const targetKey = `${acc.key}::`;
    const target = merged.get(targetKey);
    const noteFragment = `plus a ${acc.unit}`;
    if (target) {
      target.notes.add(noteFragment);
      acc.notes.forEach((n) => target.notes.add(n));
      acc.sources.forEach((s) => target.sources.add(s));
      if (acc.displayName.length > target.displayName.length) {
        target.displayName = acc.displayName;
      }
      merged.delete(mapKey);
    } else {
      // Promote this vague entry to be the unit-less canonical entry.
      acc.unit = "";
      acc.qtySum = null;
      acc.notes.add(noteFragment);
      merged.delete(mapKey);
      merged.set(targetKey, acc);
    }
  }

  // ---- Prefix-collapse pass ----
  // Where one canonical key is a strict prefix of another AND the extra
  // tokens are all stripped descriptors / prep words, fold the longer one
  // into the shorter one (same unit only — never lie about quantities).
  const isCollapsibleSuffix = (extra: string[]): boolean => {
    if (extra.length === 0) return false;
    return extra.every((w) =>
      TRAILING_DESCRIPTORS.has(w) ||
      w === "for" || w === "to" || w === "the" || w === "a" || w === "of" ||
      w === "serve" || w === "serving" || w === "garnish" || w === "taste",
    );
  };

  const entries = Array.from(merged.entries());
  // Group entries by unit so we only consider in-unit collapses.
  const byUnit = new Map<string, [string, Accumulator][]>();
  for (const e of entries) {
    const u = e[1].unit;
    if (!byUnit.has(u)) byUnit.set(u, []);
    byUnit.get(u)!.push(e);
  }

  for (const group of byUnit.values()) {
    // Sort shortest key first so prefixes are processed before their extensions.
    group.sort((a, b) => a[1].key.split(" ").length - b[1].key.split(" ").length);
    for (let i = 0; i < group.length; i++) {
      const [shortKey, shortAcc] = group[i];
      if (!merged.has(shortKey)) continue;
      const shortTokens = shortAcc.key.split(" ");
      for (let j = i + 1; j < group.length; j++) {
        const [longKey, longAcc] = group[j];
        if (!merged.has(longKey)) continue;
        const longTokens = longAcc.key.split(" ");
        if (longTokens.length <= shortTokens.length) continue;
        const startsWith = shortTokens.every((t, idx) => longTokens[idx] === t);
        if (!startsWith) continue;
        const extra = longTokens.slice(shortTokens.length);
        if (!isCollapsibleSuffix(extra)) continue;

        // Merge longAcc into shortAcc.
        if (longAcc.displayName.length > shortAcc.displayName.length) {
          shortAcc.displayName = longAcc.displayName;
        }
        if (longAcc.qtySum != null) {
          shortAcc.qtySum = (shortAcc.qtySum ?? 0) + longAcc.qtySum;
        }
        longAcc.notes.forEach((n) => shortAcc.notes.add(n));
        longAcc.sources.forEach((s) => shortAcc.sources.add(s));
        merged.delete(longKey);
      }
    }
  }

  const items: ShoppingItem[] = Array.from(merged.values()).map((m, idx) => ({
    id: cryptoRandomId(),
    name: m.displayName,
    quantity: m.qtySum != null ? formatQuantity(m.qtySum) : "",
    unit: m.unit,
    note: Array.from(m.notes).join("; "),
    department: m.department,
    checked: false,
    position: idx,
    custom: false,
    sources: Array.from(m.sources),
    confidence: m.confidence,
  }));

  const orderIndex = new Map<Department, number>();
  const order = [
    "Produce", "Meat & Fish", "Dairy & Eggs", "Bakery", "Dry Goods",
    "Herbs & Spices", "Tins & Jars", "Oils, Sauces & Condiments",
    "Frozen", "Drinks", "Snacks", "Household / Other",
  ] as Department[];
  order.forEach((d, i) => orderIndex.set(d, i));

  items.sort((a, b) => {
    const da = orderIndex.get(a.department) ?? 999;
    const db = orderIndex.get(b.department) ?? 999;
    if (da !== db) return da - db;
    return a.name.localeCompare(b.name);
  });

  return items.map((it, i) => ({ ...it, position: i }));
};

const cryptoRandomId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
};

export { cryptoRandomId, canonicalKey };
